import React, {useState, useCallback, useEffect} from 'react'
import {Input, Spin, Form} from 'antd'
import {debounce} from 'lodash'
import {useTranslation} from 'react-i18next'
import {validatePostalCodeOfUK} from 'dna-common'
import {FormItem} from '@/components'
import {useOnboardingStore} from '@/stores/onboarding'
import {onboardingMessage} from '@/pages/onboarding/utils'

import styles from './PostalCodeBlock.scss'

export interface PostalCodeBlockProps {
    form?: any
    fieldPrefix?: string
    required?: boolean
    disabled?: boolean
    labels?: {
        postalCode?: string
        addressLine1?: string
        addressLine2?: string
        townOrCity?: string
    }
    placeholders?: {
        postalCode?: string
        addressLine1?: string
        addressLine2?: string
        townOrCity?: string
    }
    className?: string
    inputClassName?: string
    showOptionalLabel?: boolean
    optionalLabelText?: string
    isFieldDisabled?: (fieldName: string) => boolean
    showPostcodeAlert?: boolean
    postcodeAlertText?: string
}

export const PostalCodeBlock: React.FC<PostalCodeBlockProps> = ({
    form,
    fieldPrefix = '',
    required = false,
    disabled = false,
    labels = {},
    placeholders = {},
    className = '',
    inputClassName = '',
    showOptionalLabel = false,
    optionalLabelText = '(optional)',
    isFieldDisabled,
    showPostcodeAlert = false,
    postcodeAlertText = 'Enter a valid UK postcode to auto-populate address fields'
}) => {
    const [addressOptions, setAddressOptions] = useState<
        {value: string; label: string; address?: any}[]
    >([])
    const [isSearchingAddress, setIsSearchingAddress] = useState(false)
    const [showAddressDropdown, setShowAddressDropdown] = useState(false)
    const [currentPostcode, setCurrentPostcode] = useState('')

    const store = useOnboardingStore()
    const { t } = useTranslation()

    const defaultLabels = {
        postalCode: t('PostalCodeBlock.labels.postalCode'),
        addressLine1: t('PostalCodeBlock.labels.addressLine1'),
        addressLine2: t('PostalCodeBlock.labels.addressLine2'),
        townOrCity: t('PostalCodeBlock.labels.townOrCity')
    }

    const defaultPlaceholders = {
        postalCode: t('PostalCodeBlock.placeholders.postalCode'),
        addressLine1: t('PostalCodeBlock.placeholders.addressLine1'),
        addressLine2: t('PostalCodeBlock.placeholders.addressLine2'),
        townOrCity: t('PostalCodeBlock.placeholders.townOrCity')
    }

    const mergedLabels = {...defaultLabels, ...labels}
    const mergedPlaceholders = {...defaultPlaceholders, ...placeholders}

    const getFieldName = (fieldName: string) => {
        return fieldPrefix ? `${fieldPrefix}.${fieldName}` : fieldName
    }

    // Watch form postcode field for external changes
    const watchedPostcode = Form.useWatch(getFieldName('postalCode'), form)

    // Update local state when form field changes externally
    useEffect(() => {
        const formPostcode = watchedPostcode || ''
        if (formPostcode !== currentPostcode) {
            setCurrentPostcode(formPostcode)
            setShowAddressDropdown(false)
            setAddressOptions([])
        }
    }, [watchedPostcode, currentPostcode])

    const searchAddressByPostcode = useCallback(
        debounce(async (postcode: string) => {
            if (!postcode || !validatePostalCodeOfUK(postcode)) {
                setAddressOptions([])
                setShowAddressDropdown(false)
                return
            }

            setIsSearchingAddress(true)
            try {
                const addresses = await store.searchAddressByPostalCode(postcode)
                const options = addresses.map((address: any, index: number) => {
                    const line1 = address.line_1 || address.addressLine1 || ''
                    const line2 = address.line_2 || address.addressLine2 || ''
                    const town = address.town_or_city || address.townOrCity || ''
                    const postCode = address.postcode || address.postalCode || postcode

                    let label = line1
                    if (line2) label += `, ${line2}`
                    if (town) label += `, ${town}`
                    if (postCode) label += `, ${postCode}`

                    return {
                        value: `${index}`,
                        label: label,
                        address: address
                    }
                })
                setAddressOptions(options)
                setShowAddressDropdown(options.length > 0)
            } catch (error) {
                onboardingMessage.error('Failed to search addresses')
                setAddressOptions([])
                setShowAddressDropdown(false)
            } finally {
                setIsSearchingAddress(false)
            }
        }, 500),
        [store]
    )

    const handleAddressSelect = (addressIndex: string) => {
        const selectedOption = addressOptions.find((opt) => opt.value === addressIndex)
        if (selectedOption && selectedOption.address && form) {
            const address = selectedOption.address

            const fieldsToUpdate = {
                [getFieldName('addressLine1')]: address.line_1 || address.addressLine1 || '',
                [getFieldName('addressLine2')]: address.line_2 || address.addressLine2 || '',
                [getFieldName('townOrCity')]: address.town_or_city || address.townOrCity || '',
                [getFieldName('country')]: address.country || 'United Kingdom',
                [getFieldName('postalCode')]:
                    address.postcode ||
                    address.postalCode ||
                    form.getFieldValue(getFieldName('postalCode'))
            }

            form.setFieldsValue(fieldsToUpdate)
            setShowAddressDropdown(false)
            setAddressOptions([])
        }
    }

    const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase()
        setCurrentPostcode(value)
        if (form) {
            form.setFieldValue(getFieldName('postalCode'), value)
        }
        searchAddressByPostcode(value)
    }

    const getFieldDisabledState = (fieldName: string) => {
        if (isFieldDisabled) {
            return isFieldDisabled('residentialAddress') || disabled
        }
        return disabled
    }

    return (
        <div className={`postal-code-block ${className}`}>
            <div className={styles.postcodeContainer}>
                <FormItem
                    name={getFieldName('postalCode')}
                    label={mergedLabels.postalCode}
                    required={required}
                    {...(showPostcodeAlert && {
                        onboardingAlert: {
                            text: postcodeAlertText,
                            variant: 'info' as const,
                            trigger: 'hover' as const
                        }
                    })}
                >
                    <Input
                        placeholder={mergedPlaceholders.postalCode}
                        className={inputClassName}
                        onChange={handlePostcodeChange}
                        disabled={getFieldDisabledState('postalCode')}
                        suffix={isSearchingAddress && <Spin size='small' />}
                        value={currentPostcode}
                    />
                </FormItem>

                {showAddressDropdown && addressOptions.length > 0 && (
                    <div className={styles.addressDropdownContainer}>
                        {addressOptions.map((option) => (
                            <div
                                key={option.value}
                                className={styles.addressOption}
                                onClick={() => handleAddressSelect(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <FormItem
                name={getFieldName('addressLine1')}
                label={mergedLabels.addressLine1}
                required={required}
            >
                <Input
                    placeholder={mergedPlaceholders.addressLine1}
                    className={inputClassName}
                    disabled={getFieldDisabledState('addressLine1')}
                />
            </FormItem>

            <FormItem
                name={getFieldName('addressLine2')}
                label={
                    showOptionalLabel ? (
                        <span>
                            {mergedLabels.addressLine2}
                            <span className={styles.optional}> {optionalLabelText}</span>
                        </span>
                    ) : (
                        mergedLabels.addressLine2
                    )
                }
            >
                <Input
                    placeholder={mergedPlaceholders.addressLine2}
                    className={inputClassName}
                    disabled={getFieldDisabledState('addressLine2')}
                />
            </FormItem>

            <FormItem
                name={getFieldName('townOrCity')}
                label={mergedLabels.townOrCity}
                required={required}
            >
                <Input
                    placeholder={mergedPlaceholders.townOrCity}
                    className={inputClassName}
                    disabled={getFieldDisabledState('townOrCity')}
                />
            </FormItem>
        </div>
    )
}
